import { Button, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import CloseIcon from "@mui/icons-material/Close"
import { Card, CardTitle, Content, Main, TitleAccent } from "./styled"
import { ReportDetail } from "./parts/ReportDetail"
import { MobileLayout } from "./utils"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import {
    ImageViewerOverlay,
    useImageViewerOverlay
} from "@/pages/OperationInfoPage/parts/ImageViewerOverlay"
import { ImmediatePhotoItem } from "@/hooks/useImmediatePhotoUpload"
import { LostItemCategory } from "@/hooks/useLostItemReport"
import { useTranslation } from "@/hooks/useTranslation"

interface Props {
    category: LostItemCategory
    itemDescription: string
    storagePlace: string
    note: string
    photos: ImmediatePhotoItem[]
    submitting: boolean
    layout: MobileLayout
    onSubmit: () => void
    onBack: () => void
    onClose: () => void
}

/**
 * 送信前の内容確認。送信すると WORK へ通知が飛び、通知後は取り消せなくなるため、
 * 備品の不足報告と同じく確認のワンクッションを挟む。
 */
export const ConfirmView = ({
    category,
    itemDescription,
    storagePlace,
    note,
    photos,
    submitting,
    layout,
    onSubmit,
    onBack,
    onClose
}: Props) => {
    const { t } = useTranslation()

    const { openedImage, openImageViewer, closeImageViewer } =
        useImageViewerOverlay()

    return (
        <Main style={layout.rootProps.style}>
            <Header {...layout.headerProps}>
                <MobileAppHeader
                    title={
                        <Typography variant="h4">{t("内容の確認")}</Typography>
                    }
                    onPrev={onBack}
                    onNext={onClose}
                    nextIcon={<CloseIcon />}
                />
            </Header>

            <div {...layout.bodyProps}>
                <Content>
                    <Typography>
                        {t("以下の内容で忘れ物を報告します。")}
                    </Typography>

                    <Card elevation={1}>
                        <CardTitle>
                            <TitleAccent />
                            {t("報告する内容")}
                        </CardTitle>

                        <ReportDetail
                            category={category}
                            itemDescription={itemDescription}
                            storagePlace={storagePlace}
                            note={note}
                            // 圧縮中の写真が残っていると確認画面へ進めないため、ここは確定分だけでよい
                            photos={photos.map(photo => ({
                                id: photo.id,
                                url: photo.objectURL
                            }))}
                            onOpenPhoto={photo =>
                                openImageViewer({
                                    id: photo.id,
                                    imageUrl: photo.url
                                })
                            }
                        />
                    </Card>
                </Content>
            </div>

            <ReportFooter {...layout.footerProps}>
                <Button
                    variant="filled"
                    color="primary"
                    fullWidth
                    disabled={submitting}
                    onClick={onSubmit}
                >
                    {t("この内容で報告する")}
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    disabled={submitting}
                    onClick={onBack}
                    css={css`
                        margin-top: 8px;
                    `}
                >
                    {t("修正する")}
                </Button>
            </ReportFooter>

            <ImageViewerOverlay
                openedImage={openedImage}
                onClose={closeImageViewer}
            />
        </Main>
    )
}
